import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import GlassCard from '@/components/base/GlassCard';
import KpiCard from '@/components/base/KpiCard';
import { leaderboardData, growthTrendData } from '@/mocks/phase2Data';

const rankBadge = (rank: number) => {
  if (rank === 1) return { bg: 'linear-gradient(135deg,#ffd700,#ffaa00)', color: '#fff' };
  if (rank === 2) return { bg: 'linear-gradient(135deg,#c0c0c0,#909090)', color: '#fff' };
  if (rank === 3) return { bg: 'linear-gradient(135deg,#cd7f32,#a05a00)', color: '#fff' };
  return { bg: 'rgba(255,77,77,0.2)', color: '#ff6b47' };
};

const LeaderboardPage: React.FC = () => {
  const topByGrowth = [...leaderboardData].sort((a, b) => b.buyume - a.buyume).slice(0, 5);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Leaderboard & Büyüme</h1>
        <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>En iyi uzmanlar ve büyüme sıralaması</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Toplam Uzman" value={leaderboardData.length} change={5.1} icon="ri-team-line" />
        <KpiCard label="En Yüksek Kazanç" value="18.450" icon="ri-trophy-line" suffix=" ₺" />
        <KpiCard label="Maks. Büyüme" value={41.3} change={8.4} icon="ri-rocket-line" suffix="%" />
        <KpiCard label="Ort. Puan" value="4.4" change={0.3} icon="ri-star-line" suffix="/5" />
      </div>

      {/* Growth trend chart */}
      <GlassCard title="Top 3 Uzman Gelişim Grafiği" subtitle="Son 6 ay — aylık kazanç (₺)" className="mb-4">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={growthTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="ay" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,77,77,0.3)', borderRadius: '8px', fontSize: '12px' }}
              formatter={(v: number, name: string) => [`${v.toLocaleString('tr-TR')} ₺`, name]}
            />
            <Legend wrapperStyle={{ fontSize: '11px', color: '#9ca3af' }} />
            <Line type="monotone" dataKey="kemal" name="Kemal Çelik" stroke="#ff4d4d" strokeWidth={2.5} dot={{ r: 4, fill: '#ff4d4d' }} />
            <Line type="monotone" dataKey="ali" name="Ali Arslan" stroke="#ff8c42" strokeWidth={2} dot={{ r: 3, fill: '#ff8c42' }} />
            <Line type="monotone" dataKey="ahmet" name="Ahmet Yılmaz" stroke="#ffa94d" strokeWidth={2} dot={{ r: 3, fill: '#ffa94d' }} />
          </LineChart>
        </ResponsiveContainer>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main leaderboard */}
        <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <h3 className="text-sm font-semibold text-white">Top 10 Uzman</h3>
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,77,77,0.15)', color: '#ff6b47' }}>Bu Ay</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Sıra', 'Uzman', 'Puan', 'İş', 'Kazanç', 'Büyüme'].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((e, idx) => {
                  const badge = rankBadge(e.rank);
                  return (
                    <tr
                      key={e.rank}
                      className="transition-all duration-200"
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
                      }}
                      onMouseEnter={(el) => { (el.currentTarget as HTMLTableRowElement).style.background = 'rgba(255,77,77,0.05)'; }}
                      onMouseLeave={(el) => { (el.currentTarget as HTMLTableRowElement).style.background = idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent'; }}
                    >
                      <td className="px-4 py-3">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: badge.bg, color: badge.color }}>{e.rank}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)' }}>{e.avatar}</div>
                          <div>
                            <div className="text-white font-medium text-sm">{e.isim}</div>
                            <div className="text-xs" style={{ color: '#6b7280' }}>{e.sehir}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-xs" style={{ color: '#ff8c42' }}></i>
                          <span className="text-white font-medium">{e.puan}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white">{e.is}</td>
                      <td className="px-4 py-3 font-bold text-white text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{e.kazanc.toLocaleString('tr-TR')} ₺</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#10b981' }}>
                          <i className="ri-arrow-up-line text-xs"></i>%{e.buyume}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Fastest growing */}
        <GlassCard title="En Hızlı Büyüyen" subtitle="Büyüme % sıralaması">
          <div className="space-y-3">
            {topByGrowth.map((e, idx) => (
              <div key={e.rank} className="p-3 rounded-xl transition-all hover:-translate-y-0.5 duration-200" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)' }}>{e.avatar}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{e.isim}</div>
                    <div className="text-xs" style={{ color: '#6b7280' }}>{e.sehir}</div>
                  </div>
                  <span className="text-sm font-bold" style={{ color: '#10b981' }}>+%{e.buyume}</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: `${(e.buyume / topByGrowth[0].buyume) * 100}%`,
                      background: `linear-gradient(90deg, #10b981, #34d399)`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default LeaderboardPage;
